// File: frontend/src/components/StixVisualization.tsx
import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { Box, Paper, Typography, Alert, Chip, alpha } from '@mui/material';
import { cyber } from '../theme';

interface StixObject {
    id: string;
    type: string;
    name?: string;
    [key: string]: any;
}

interface StixRelationship {
    id: string;
    type: 'relationship';
    source_ref: string;
    target_ref: string;
    relationship_type: string;
}

interface StixBundle {
    type: 'bundle';
    id: string;
    objects: (StixObject | StixRelationship)[];
}

interface StixVisualizationProps {
    stixJsonString: string;
}

// Color mapping for STIX object types (optimized for dark background)
const STIX_COLORS: { [key: string]: string } = {
    'threat-actor': '#FF6B6B',        // Rosso vivace
    'malware': '#FF9F43',             // Arancione intenso
    'attack-pattern': '#FFD93D',      // Giallo brillante
    'tool': '#6BCF7F',                // Verde lime
    'campaign': '#54D8C7',            // Turchese
    'intrusion-set': '#4ECDC4',       // Ciano
    'indicator': '#95E1D3',           // Verde acqua chiaro
    'vulnerability': '#F38181',       // Rosa corallo
    'course-of-action': '#A29BFE',    // Lavanda
    'identity': '#FD79A8',            // Rosa vivace
    'infrastructure': '#74B9FF',      // Azzurro
    'location': '#FDCB6E',            // Oro
    'malware-analysis': '#E17055',    // Arancione bruciato
    'note': '#B2BEC3',                // Grigio chiaro
    'observed-data': '#DFE6E9',       // Grigio molto chiaro
    'opinion': '#FAB1A0',             // Pesca
    'report': '#A29BFE',              // Lavanda
    'grouping': '#74B9FF',            // Azzurro
};

const DEFAULT_COLOR = '#95A5A6';      // Grigio medio

const StixVisualization: React.FC<StixVisualizationProps> = ({ stixJsonString }) => {
    const svgRef = useRef<SVGSVGElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [error, setError] = useState<string | null>(null);
    const [stats, setStats] = useState<{ nodes: number; relationships: number } | null>(null);
    const [selectedNode, setSelectedNode] = useState<StixObject | null>(null);

    useEffect(() => {
        if (!svgRef.current || !containerRef.current || !stixJsonString) return;

        try {
            const bundle: StixBundle = JSON.parse(stixJsonString);
            
            if (!bundle.objects || bundle.objects.length === 0) {
                setError('STIX bundle contains no objects');
                return;
            }

            // Separate nodes and relationships
            const nodes: StixObject[] = bundle.objects.filter(obj => obj.type !== 'relationship') as StixObject[];
            const relationships: StixRelationship[] = bundle.objects.filter(obj => obj.type === 'relationship') as StixRelationship[];

            setStats({ nodes: nodes.length, relationships: relationships.length });

            // Create a map for quick node lookup
            const nodeMap = new Map(nodes.map(node => [node.id, node]));

            // Create links data structure for D3
            const links = relationships
                .filter(rel => nodeMap.has(rel.source_ref) && nodeMap.has(rel.target_ref))
                .map(rel => ({
                    source: rel.source_ref,
                    target: rel.target_ref,
                    type: rel.relationship_type,
                    id: rel.id
                }));

            // Clear previous visualization
            d3.select(svgRef.current).selectAll('*').remove();

            // Get container dimensions
            const container = containerRef.current;
            const width = container.clientWidth;
            const height = 800;

            // Create SVG
            const svg = d3.select(svgRef.current)
                .attr('width', width)
                .attr('height', height)
                .attr('viewBox', [0, 0, width, height]);

            // Add zoom behavior
            const g = svg.append('g');
            
            const zoom = d3.zoom<SVGSVGElement, unknown>()
                .scaleExtent([0.1, 4])
                .on('zoom', (event) => {
                    g.attr('transform', event.transform);
                });

            svg.call(zoom as any);

            // Create force simulation
            const simulation = d3.forceSimulation(nodes as any)
                .force('link', d3.forceLink(links)
                    .id((d: any) => d.id)
                    .distance(150))
                .force('charge', d3.forceManyBody().strength(-300))
                .force('center', d3.forceCenter(width / 2, height / 2))
                .force('collision', d3.forceCollide().radius(50));

            // Create arrow markers for directed edges
            svg.append('defs').selectAll('marker')
                .data(['end'])
                .enter().append('marker')
                .attr('id', 'arrowhead')
                .attr('viewBox', '0 -5 10 10')
                .attr('refX', 25)
                .attr('refY', 0)
                .attr('markerWidth', 6)
                .attr('markerHeight', 6)
                .attr('orient', 'auto')
                .append('path')
                .attr('d', 'M0,-5L10,0L0,5')
                .attr('fill', '#666');             // Grigio medio come i link

            // Drag functions (defined before use)
            const dragstarted = (event: any, d: any) => {
                if (!event.active) simulation.alphaTarget(0.3).restart();
                d.fx = d.x;
                d.fy = d.y;
            };

            const dragged = (event: any, d: any) => {
                d.fx = event.x;
                d.fy = event.y;
            };

            const dragended = (event: any, d: any) => {
                if (!event.active) simulation.alphaTarget(0);
                d.fx = null;
                d.fy = null;
            };

            // Create links
            const link = g.append('g')
                .selectAll('line')
                .data(links)
                .enter().append('line')
                .attr('stroke', '#666')            // Grigio medio per i link
                .attr('stroke-opacity', 0.7)       // Leggermente più opachi
                .attr('stroke-width', 2)
                .attr('marker-end', 'url(#arrowhead)');

            // Create link labels
            const linkLabel = g.append('g')
                .selectAll('text')
                .data(links)
                .enter().append('text')
                .attr('font-size', 10)
                .attr('fill', '#b0b0b0')           // Grigio chiaro per le label dei link
                .attr('text-anchor', 'middle')
                .text(d => d.type);

            // Create nodes
            const node = g.append('g')
                .selectAll('circle')
                .data(nodes)
                .enter().append('circle')
                .attr('r', 20)
                .attr('fill', d => STIX_COLORS[d.type] || DEFAULT_COLOR)
                .attr('stroke', '#1a1d2e')         // Bordo scuro come lo sfondo
                .attr('stroke-width', 2)
                .style('cursor', 'pointer')
                .call(d3.drag<any, any>()
                    .on('start', dragstarted)
                    .on('drag', dragged)
                    .on('end', dragended) as any);

            // Add node labels
            const nodeLabel = g.append('g')
                .selectAll('text')
                .data(nodes)
                .enter().append('text')
                .attr('text-anchor', 'middle')
                .attr('dy', 35)
                .attr('font-size', 12)
                .attr('fill', '#e0e0e0')           // Testo bianco/chiaro
                .attr('font-weight', '500')         // Leggermente bold per leggibilità
                .style('pointer-events', 'none')
                .text(d => d.name || d.type);

            // Add tooltips and click handlers
            node.on('mouseover', function(event, d) {
                d3.select(this)
                    .transition()
                    .duration(200)
                    .attr('r', 25);
            })
            .on('mouseout', function(event, d) {
                d3.select(this)
                    .transition()
                    .duration(200)
                    .attr('r', 20);
            })
            .on('click', function(event, d) {
                setSelectedNode(d as StixObject);
            });

            // Update positions on simulation tick
            simulation.on('tick', () => {
                link
                    .attr('x1', (d: any) => d.source.x)
                    .attr('y1', (d: any) => d.source.y)
                    .attr('x2', (d: any) => d.target.x)
                    .attr('y2', (d: any) => d.target.y);

                linkLabel
                    .attr('x', (d: any) => (d.source.x + d.target.x) / 2)
                    .attr('y', (d: any) => (d.source.y + d.target.y) / 2);

                node
                    .attr('cx', (d: any) => d.x)
                    .attr('cy', (d: any) => d.y);

                nodeLabel
                    .attr('x', (d: any) => d.x)
                    .attr('y', (d: any) => d.y);
            });

            setError(null);

        } catch (err) {
            setError(`Failed to parse or visualize STIX bundle: ${err instanceof Error ? err.message : String(err)}`);
        }

    }, [stixJsonString]);

    if (error) {
        return <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>;
    }

    return (
        <Box>
            {stats && (
                <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip label={`${stats.nodes} Objects`} color="primary" size="small" />
                    <Chip label={`${stats.relationships} Relationships`} color="secondary" size="small" />
                </Box>
            )}
            
            <Paper 
                ref={containerRef} 
                sx={{
                    position: 'relative',
                    width: '100%',
                    bgcolor: cyber.bgDefault,      // Sfondo scuro
                    border: `1px solid ${cyber.border}`,
                    borderRadius: 1,
                    overflow: 'hidden'
                }}
            >
                <svg ref={svgRef} style={{ display: 'block' }} />
                
                <Box sx={{
                    position: 'absolute',
                    top: 10,
                    right: 10,
                    bgcolor: alpha(cyber.bgPaper, 0.9),   // Sfondo scuro trasparente
                    border: `1px solid ${cyber.border}`,  // Bordo per definizione
                    p: 1,
                    borderRadius: 1,
                    fontSize: '0.75rem',
                    color: '#e0e0e0'                      // Testo chiaro
                }}>
                    💡 Drag nodes • Scroll to zoom • Click for details
                </Box>
            </Paper>

            {/* Selected Node Details */}
            {selectedNode && (
                <Paper sx={{ mt: 2, p: 2, bgcolor: cyber.bgDefault, border: `1px solid ${cyber.border}` }}>
                    <Typography variant="h6" gutterBottom sx={{ color: '#e0e0e0' }}>
                        Selected: {selectedNode.name || selectedNode.type}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                        <Chip label={selectedNode.type} size="small" color="primary" sx={{ fontFamily: cyber.fontMono }} />
                        {selectedNode.id && <Chip label={`ID: ${selectedNode.id.split('--')[1]?.substring(0, 8)}...`} size="small" variant="outlined" sx={{ fontFamily: cyber.fontMono, color: alpha(cyber.accent.cyan, 0.9), borderColor: alpha(cyber.accent.cyan, 0.4) }} />}
                    </Box>
                    <Paper
                        component="pre"
                        sx={{
                            p: 2,
                            maxHeight: '300px',
                            overflow: 'auto',
                            bgcolor: '#15172a',        // Sfondo scuro per il JSON
                            border: `1px solid ${cyber.border}`,
                            fontSize: '0.75rem',
                            fontFamily: cyber.fontMono,
                            color: '#cdd2ea'           // Testo chiaro
                        }}
                    >
                        {JSON.stringify(selectedNode, null, 2)}
                    </Paper>
                </Paper>
            )}

            {/* Legend */}
            <Paper sx={{ mt: 2, p: 2, bgcolor: cyber.bgDefault, border: `1px solid ${cyber.border}` }}>
                <Typography variant="subtitle2" gutterBottom sx={{ color: '#e0e0e0' }}>
                    STIX Object Types Legend
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {Object.entries(STIX_COLORS).map(([type, color]) => (
                        <Box key={type} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box sx={{
                                width: 16,
                                height: 16,
                                borderRadius: '50%',
                                bgcolor: color,
                                border: '2px solid #1a1d2e'    // Bordo scuro come i nodi
                            }} />
                            <Typography variant="caption" sx={{ color: '#b0b0b0', fontFamily: cyber.fontMono, fontSize: '0.65rem' }}>
                                {type}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Paper>
        </Box>
    );
};

export default StixVisualization;